import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import { DateTime } from 'luxon'
import EventFilters from './EventFilters'
import EventsContainer from './EventsContainer'
import Day from './Day'
import List from './List'
import '@fullcalendar/common/main.css'
import queryString from 'query-string'

const parent = (node, klass) => {
  let current = node,
    result = null
  while (
    current.parentNode != null &&
    current.parentNode != document.documentElement &&
    !result
  ) {
    if (current.parentNode.classList.contains(klass)) {
      result = current.parentNode
    }
    current = current.parentNode
  }
  return result
}

const Calendar = styled.div`
  padding: 30px 0 0;

  @media screen and (min-width: 960px) {
    padding: 82px 38px 0;
  }

  ${({ fullWidth, filters }) => `
    ${
      !fullWidth && filters
        ? `
          @media screen and (min-width: 1200px) {
            display: grid;
            grid-template-columns: 1fr 1fr;
          }
        `
      : !fullWidth
        ? `
          @media screen and (min-width: 960px) {
            display: grid;
            grid-template-columns: 1fr 1fr;
          }
        `  : ''
    }

    ${
      fullWidth
        ? `
          .fc {
            padding: 0;
            background: 0;
          }

          .fc-toolbar.fc-header-toolbar {
            width: 100%;
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;
            padding-left: 9%;
            padding-right: 9%;
          }

          .fc-view-harness {
            display: none;
          }
        `
        : ''
    }
  `}
`

const Events = () => {
  const [categories, setCategories] = useState([])
  const [events, setEvents] = useState([])
  const [view, setView] = useState('dayGridMonth')
  const [selectedDay, setSelectedDay] = useState(DateTime.local())
  const [monthEvents, setMonthEvents] = useState([])
  const [dayEvents, setDayEvents] = useState([])
  const [filters, setFilters] = useState({
    categories: [],
    query: '',
  })
  const [filterInit, setFilterInit] = useState(false)

  const calendarRef = useRef(null)

  useEffect(() => {
    const parsed = queryString.parse(location.search)

    if (parsed.category) {
      setFilters({
        ...filters,
        categories: [parsed.category],
      })
    }

    setFilterInit(true)
  }, [])

  useEffect(() => {
    if (filterInit) {
      const query = queryString.stringify(filters, {
        arrayFormat: 'bracket',
      })

      fetch(`/events.json?${query}`)
        .then((response) => response.json())
        .then((data) => {
          const days = document.querySelectorAll('.fc-daygrid-day-number')

          Array.from(days).forEach((day) => day.classList.remove('has-event'))

          const loadedEvents = []
          data.events.forEach((event) => {
            if (event.formatted) {
              const formattedDate = DateTime.fromFormat(
                event.formatted,
                'EEEE, MMMM dd, yyyy h:mm a',
              )

              event.start = formattedDate.toISO()
              loadedEvents.push(event)
            } else {
              loadedEvents.push(event)
            }
          })

          setCategories(data.categories)
          setEvents(loadedEvents)
        })
    }
  }, [filters, filterInit])

  useEffect(() => {
    highlightSelected()

    if (events.length) {
      getDayEvents()
      getMonthEvents()
    } else {
      getMonthEvents()
    }
  }, [events, selectedDay])

  useEffect(() => {
    const calendarApi = calendarRef.current.getApi()
    calendarApi.changeView(view)
  }, [view])

  const handleDateClick = (arg) => {
    setSelectedDay(DateTime.fromISO(arg.dateStr))
  }

  const getMonthEvents = (dates) => {
    let month = 0
    let year = 0

    if (dates) {
      const calendarDate = DateTime.fromISO(dates.startStr)
      month = calendarDate.month
      year = calendarDate.year
    } else if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      const calendarDate = DateTime.fromISO(calendarApi.getDate().toISOString())
      month = calendarDate.month
      year = calendarDate.year
    }

    const foundEvents = events.filter((event) => {
      const eventDate = DateTime.fromISO(event.start)

      return month === eventDate.month && year === eventDate.year
    })

    setMonthEvents(foundEvents)
  }

  const getDayEvents = () => {
    const foundEvents = events.filter((event) => {
      return (
        DateTime.fromISO(event.start).toLocaleString(DateTime.DATE_SHORT) ===
        selectedDay.toLocaleString(DateTime.DATE_SHORT)
      )
    })

    setDayEvents(foundEvents)
  }

  const highlightSelected = () => {
    const formattedDate = selectedDay.toFormat('yyyy-MM-dd')
    const selected = document.querySelector(
      `.fc-daygrid-day[data-date="${formattedDate}"] .fc-daygrid-day-number`,
    )

    const allNumbers = document.querySelectorAll('.fc-daygrid-day-number')
    Array.from(allNumbers).forEach((number) => {
      number.classList.remove('active')
    })

    if (selected) {
      selected.classList.add('active')
    }
  }

  const highlightDays = ({ el }) => {
    if (view === 'dayGridMonth') {
      const day = parent(el, 'fc-daygrid-day-frame').querySelector(
        '.fc-daygrid-day-number',
      )

      day.classList.add('has-event')
    }
  }

  const showDate = (date, eventIndex) => {
    const eventDays = []
    const month = DateTime.fromISO(date).toFormat('LLL')
    const day = DateTime.fromISO(date).toFormat('dd')
    const dateString = `${month}${day}`

    monthEvents
      .sort((a, b) => (a.start > b.start ? 1 : -1))
      .forEach((event, index) => {
        eventDays.push({
          date: DateTime.fromISO(event.start).toFormat('LLLdd'),
          index,
        })
      })

    const foundDate = eventDays.filter((e) => e.date === dateString)

    if (foundDate.length === 1) {
      return true
    } else if (foundDate.length > 1 && eventIndex > foundDate[0].index) {
      return false
    } else {
      return true
    }
  }

  const showList = view => {
    return (
      <List className={view === 'dayGridMonth' ? 'list-container' : ''}>
        {monthEvents.length > 0 ? (
          <ul>
            {monthEvents
              .sort((a, b) => (a.start > b.start ? 1 : -1))
              .map((event, index) => (
                <li
                  className={`event-card ${
                    !showDate(event.start, index)
                      ? 'event-card--same-day'
                      : ''
                  }`}
                  key={index}
                >
                  {showDate(event.start, index) ? (
                    <div className='event-card__date'>
                      <h4 className='event-card__date-text'>
                        {DateTime.fromISO(event.start).toFormat('LLL')}
                        <span>
                          {DateTime.fromISO(event.start).toFormat('dd')}
                        </span>
                      </h4>
                    </div>
                  ) : (
                    <div />
                  )}

                  <div className='event-card__details'>
                    <h3>{event.title}</h3>

                    {event.excerpt && (
                      <div
                        className='event-card__excerpt'
                        dangerouslySetInnerHTML={{
                          __html: event.excerpt,
                        }}
                      />
                    )}

                    <a href={event.url} className='underlined-link'>
                      See Event Details
                    </a>
                  </div>
                </li>
              ))}
          </ul>
        ) : (
          <p>There are no events on this month</p>
        )}
      </List>
    )
  }

  return (
    <EventsContainer>
      <EventFilters
        categories={categories}
        currentFilters={filters}
        filterChange={setFilters}
        viewChange={setView}
      />

      <Calendar fullWidth={view === 'listMonth'} filters={view === 'dayGridMonth' && filters.query} className={filters.query ? 'calendar-category' : ''}>
        <FullCalendar
          dateClick={handleDateClick}
          datesSet={(dates) => {
            getMonthEvents(dates)
          }}
          dayHeaderContent={(value) => {
            return DateTime.fromISO(value.date.toISOString())
              .plus({ day: 1 })
              .toFormat('ccccc')
              .toUpperCase()
          }}
          eventDidMount={highlightDays}
          events={events}
          firstDay={0}
          fixedWeekCount={false}
          headerToolbar={{
            start: 'prev',
            center: 'title',
            end: 'next',
          }}
          height='auto'
          initialView='dayGridMonth'
          showNonCurrentDates={false}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          timeZone='America/New_York'
          ref={calendarRef}
        />

        {view === 'dayGridMonth' && filters.query ? (
          showList('dayGridMonth')
        ) : view === 'dayGridMonth' ? (
          <Day>
            <h2>
              {selectedDay.toFormat('LLL')}
              <span>{selectedDay.toFormat('dd')}</span>
            </h2>

            <h4>{selectedDay.toFormat('cccc')}</h4>

            {dayEvents.length > 0 ? (
              <ul>
                {dayEvents
                  .sort((a, b) => (a.start > b.start ? 1 : -1))
                  .map((event, index) => (
                    <li key={index} className='event-card'>
                      <div className='event-card__details'>
                        <h3>{event.title}</h3>

                        <p>
                          {event.allDay
                            ? 'All Day'
                            : DateTime.fromISO(event.start)
                                .toFormat('h:mm a')
                                .toLowerCase()}
                        </p>

                        <p>{event.venue}</p>

                        <a href={event.url} className='underlined-link'>
                          See Event Details
                        </a>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <p>There are no events on this day</p>
            )}
          </Day>
        ) : (
          showList('listMonth')
        )}
      </Calendar>
    </EventsContainer>
  )
}

export default Events
